import React from 'react';
import { useTranslation } from 'react-i18next';

import { CFG } from '../../../../theme/configurator-tokens';
import { useConfigurator } from '../../store/configurator';
import { Logo } from '../../components/wizard';
import type { Country } from '../../lib/domain-types';
import {
  Backdrop,
  Panel,
  Header,
  CloseButton,
  Hairline,
  Body,
  Hero,
  HeroText,
  Title,
  Subtitle,
  SearchLabel,
  SearchInput,
  Groups,
  Empty,
  Group,
  GroupName,
  Grid,
  Row,
  FlagWrap,
  FlagImg,
  RowName,
  SoonPill,
} from './CountrySelector.styles';

const SearchIcon = (): React.ReactElement => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="11" cy="11" r="7" stroke={CFG.base400} strokeWidth="2" />
    <path d="M20 20l-3.2-3.2" stroke={CFG.base400} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CloseGlyph = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const CountryRow = ({
  country,
  selected,
  onSelect,
}: {
  country: Country;
  selected: boolean;
  onSelect: () => void;
}): React.ReactElement => (
  <Row type="button" onClick={country.isActive ? onSelect : undefined} data-ga-id={`cfg-country-row-${country.code}`} disabled={!country.isActive} $disabled={!country.isActive}>
    <FlagWrap>{country.flag ? <FlagImg src={country.flag} alt="" /> : null}</FlagWrap>
    <RowName text={country.name} $selected={selected} />
    {country.comingSoon ? <SoonPill text="Soon" tag="span" /> : null}
  </Row>
);

/**
 * Country / region picker — a full-screen overlay (logo · close, a hero card
 * with the title + search, then the grouped flag list). Selecting a region
 * switches the active currency and persists to the session via the store.
 * Ported 1:1 from the standalone app's `features/country/CountrySelector`.
 */
export const CountrySelector = (): React.ReactElement | null => {
  const { countryPickerOpen, closeCountryPicker, countryGroups, country, setCountry } = useConfigurator();
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');

  // Reset the search each time the picker opens.
  React.useEffect(() => {
    if (countryPickerOpen) setQuery('');
  }, [countryPickerOpen]);

  React.useEffect(() => {
    if (!countryPickerOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeCountryPicker();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [countryPickerOpen, closeCountryPicker]);

  // Region group headings come from the API (resolved to their English canonical in
  // `transformRegions`): translate the known set at this render site keyed on that
  // stable English value (also the React key), and pass any other CMS group name
  // through untranslated. Country NAMES are reference/locale data, left untranslated.
  const groupName = (name: string): string => {
    switch (name) {
      case 'World Wide':
        return t('World Wide');
      case 'North America':
        return t('North America');
      case 'Central America':
        return t('Central America');
      case 'South America':
        return t('South America');
      case 'America':
        return t('America');
      case 'Europe':
        return t('Europe');
      case 'Africa':
        return t('Africa');
      default:
        return name;
    }
  };

  if (!countryPickerOpen) return null;

  const q = query.trim().toLowerCase();
  const groups = countryGroups
    .map((g) => ({
      ...g,
      countries: q ? g.countries.filter((c) => c.name.toLowerCase().includes(q)) : g.countries,
    }))
    .filter((g) => g.countries.length);

  return (
    <Backdrop
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeCountryPicker();
      }}
    >
      <Panel>
        <Header>
          <Logo />
          <CloseButton type="button" onClick={closeCountryPicker} data-ga-id="cfg-country-header-close" aria-label={t('Close')}>
            <CloseGlyph />
          </CloseButton>
        </Header>
        <Hairline />

        <Body>
          <Hero>
            <HeroText>
              <Title text="Change country or region" tag="paragraph" />
              <Subtitle
                text="Selecting a country will provide tailored recommendations specific to that location"
                tag="paragraph"
              />
            </HeroText>
            <SearchLabel>
              <SearchIcon />
              <SearchInput
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('Search country or region')}
                aria-label={t('Search country or region')}
              />
            </SearchLabel>
          </Hero>

          <Groups>
            {groups.length === 0 ? (
              <Empty text="No countries match “{{query}}”." placeholders={{ query }} tag="paragraph" />
            ) : null}
            {groups.map((g) => (
              <Group key={g.name}>
                <GroupName text={groupName(g.name)} tag="paragraph" />
                <Grid>
                  {g.countries.map((c) => (
                    <CountryRow
                      key={c.code}
                      country={c}
                      selected={c.code === country?.code}
                      onSelect={() => setCountry(c.code)}
                    />
                  ))}
                </Grid>
              </Group>
            ))}
          </Groups>
        </Body>
      </Panel>
    </Backdrop>
  );
};

export default CountrySelector;
